<template>
  <span
    class="HandshakePositionMark"
    :class="{ 'is-start': align === 'start', 'has-face': showFace, 'has-side': hasSide }"
  >
    <span class="board">
      <span v-if="showFace" class="face" :class="{ 'is-passerby': standInFace }">
        <img v-if="face && !standInFace" :src="faceSrc" alt="" />
        <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="calc(var(--face-size) * 0.32)" is-current />
      </span>
      <span class="rank-line">
        <span class="rank" aria-hidden="true">
          <span
            v-for="(piece, index) in pieces"
            :key="index"
            class="square"
            :class="[index % 2 === 0 ? 'is-dark' : 'is-light', { 'is-empty-cell': hidePieces }]"
          >
            <GamChessboardPiece v-if="!hidePieces" :piece="piece" />
          </span>
        </span>
        <button v-if="photos.length" type="button" class="side-photo" @click.stop="emit('openPhoto', photos[0].hash)">
          <img :src="photoSrc(photos[0].hash)" alt="" />
          <span v-if="photos.length > 1" class="side-stack" aria-hidden="true" />
        </button>
        <span v-if="$slots.side" class="rank-side"><slot name="side" /></span>
      </span>
    </span>
    <span v-if="showNumber" class="number" :class="numberColor ? `is-${numberColor}` : undefined">
      <span class="number-lead">
        <span class="index" :class="{ 'is-slotted': hasTrail }">
          <span v-if="hasTrail" class="index-sizer" aria-hidden="true">#000</span>
          <span
            >#<template v-if="!bareNumber">{{ positionIndex }}</template></span
          >
        </span>
      </span>
      <span v-if="showPartnerDot && hasTrail" class="partner-dot">・</span>
      <span v-if="hasTrail" class="partners">
        <span v-for="name in partners" :key="name" class="partner" :class="{ 'is-unique': uniquePartner === name }"
          >@{{ name }}</span
        >
        <span
          v-for="standIn in standIns"
          :key="standIn.id"
          class="partner"
          :class="{ 'is-reported': uniqueStandIn?.id === standIn.id }"
          >{{ standIn.name }}</span
        >
      </span>
    </span>
  </span>
</template>

<script setup lang="ts">
import { gambits } from '@/core/gambits';
import GamChessboardPiece from '@/views/components/gam-chessboard/GamChessboardPiece.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { backRankFromNumber } from '@/views/main/handshakes/utils/handshake-game';
import type { StandInMark } from '@/views/main/handshakes/utils/position-collection';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    positionIndex: number;
    /** The Chess960 number under the rank. */
    showNumber?: boolean;
    /** The other players this position was checked with, next to the number. */
    partners?: string[];
    /** The partner who owns this position's unique pair. Their name is pink. */
    uniquePartner?: string | null;
    /** Passersby and ex-members marked unique who played this position, by name. Shown without an @. */
    standIns?: StandInMark[];
    /** The stand-in who owns this position's pair. Their name is the quieter pink. */
    uniqueStandIn?: StandInMark | null;
    /** The face is someone with no account: black, with a gray pawn. */
    standInFace?: boolean;
    /** Position photos. The first sits in a square at the right of the rank. */
    photos?: { hash: string; handshakeId: string }[];
    /** Requested image width. Heroes need more pixels than compact list marks. */
    photoSize?: number;
    /** Drawer rows start at the left, with the names. The handshake list centres them. */
    align?: 'start' | 'center';
    /** Pink, quieter pink, white, unknown gray, or muted unreached. Same as the table and the ring. */
    numberColor?: 'unique' | 'reported' | 'repeat' | 'unknown' | 'empty';
    /** Empty pair: the rank with no pieces on it. */
    hidePieces?: boolean;
    /** Empty pair: # with no Chess960 number. */
    bareNumber?: boolean;
    /** Latest pair: a middle dot between the number and the name. */
    showPartnerDot?: boolean;
    /** Unique pair: the player's photo above the rank, its bottom tucked under the position. */
    showFace?: boolean;
    /** Profile image hash. A missing hash keeps the white pawn. */
    face?: string | null;
  }>(),
  {
    partners: () => [],
    standIns: () => [],
    photos: () => [],
    photoSize: 64,
    uniquePartner: null,
    uniqueStandIn: null,
    standInFace: false,
    align: 'center',
    numberColor: undefined,
    hidePieces: false,
    bareNumber: false,
    showPartnerDot: false,
    showFace: false,
    face: null,
  },
);

const emit = defineEmits<{ openPhoto: [hash: string] }>();

const pieces = computed(() => backRankFromNumber(props.positionIndex));
const hasSide = computed(() => props.photos.length > 0);

/** A name follows the number, so the number keeps the width of #960. */
const hasTrail = computed(() => props.partners.length > 0 || props.standIns.length > 0);

const photoSrc = (hash: string): string => gambits.imageService.getImageSrc(hash, props.photoSize);
const faceSrc = computed(() => (props.face ? gambits.imageService.getImageSrc(props.face, 360) : ''));
</script>

<style lang="scss" scoped>
.HandshakePositionMark {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: none;
  width: min(100%, 420px);
  max-width: 100%;
}

.HandshakePositionMark.is-start {
  align-items: flex-start;
}

.board {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.face {
  --face-size: 44px;
  z-index: 0;
  align-self: center;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--face-size);
  height: var(--face-size);
  margin-bottom: -12px;
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.is-passerby {
    background: var(--color-not-played);
    color: var(--color-unknown);
  }
}

.has-side .rank-line {
  gap: 0;
}

.has-side .rank {
  z-index: 1;
  flex: 8 1 0;
  width: auto;
  margin-right: calc(100% / 9 * -0.28);
  box-shadow: 0 0 0 2px var(--color-dark-700);
}

.side-photo {
  position: relative;
  z-index: 0;
  flex: 1 1 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  min-width: 0;
  aspect-ratio: 1;
  padding: 0;
  border: 0;
  border-radius: var(--radius-x-small);
  overflow: hidden;
  background: var(--color-dark-600);
  color: inherit;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0);
  }

  &.is-empty {
    cursor: default;
  }
}

.side-stack {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    box-sizing: border-box;
    width: 6px;
    height: 6px;
    border: 1.5px solid var(--color-white);
    border-radius: 1px;
  }

  &::before {
    opacity: 0.6;
    transform: translate(calc(-50% + 1px), calc(-50% - 1px));
  }

  &::after {
    background: rgb(20, 20, 20);
    transform: translate(calc(-50% - 1px), calc(-50% + 1px));
  }
}

.rank-line {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.rank-side {
  flex: none;
  display: flex;
  align-items: center;
}

/*
 * The same rank the recommendation draws: white's back rank, dark square in
 * the left corner, pieces standing on the board's two browns.
 */
.rank {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: 1fr;
  aspect-ratio: 8 / 1;
  flex: 1;
  width: 100%;
  min-width: 0;
  border-radius: var(--radius-x-small);
  overflow: hidden;
}

.square {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  // No per-cell aspect-ratio: the 8:1 rank already makes every cell square
  // from 1fr x 1fr, and Safari does not recalculate aspect-ratio on grid items
  // during a live viewport resize, which leaves cells at stale dimensions.
}

.square.is-light {
  background: #f0d9b5;
}

.square.is-dark {
  background: #b58863;
}

.square.is-empty-cell::before {
  content: '';
  display: block;
  width: 100%;
  padding-bottom: 100%;
}

.number {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
  max-width: 100%;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--color-white-60);
}

.number.is-unique .index,
.number.is-unique .partner-dot {
  color: var(--color-pink);
}

.number.is-reported .index,
.number.is-reported .partner-dot {
  color: var(--color-reported);
}

.number.is-repeat .index,
.number.is-repeat .partner-dot {
  color: var(--color-white);
}

.number.is-unknown .index,
.number.is-unknown .partner-dot {
  color: var(--color-unknown);
}

.number.is-empty .index,
.number.is-empty .partner-dot {
  color: var(--color-white-30);
}

.partners {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  min-width: 0;
}

.partner {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: var(--color-white);

  &.is-unique {
    color: var(--color-pink);
  }

  &.is-reported {
    color: var(--color-reported);
  }
}

.HandshakePositionMark.is-start .number {
  width: 100%;
}

.number-lead {
  display: flex;
  align-items: center;
  flex: none;
  gap: 0.5rem;
}

.index {
  flex: none;
}

.index.is-slotted {
  display: inline-grid;
}

.index.is-slotted > span {
  grid-area: 1 / 1;
  align-self: start;
  justify-self: start;
}

.index-sizer {
  visibility: hidden;
}
</style>
